import { useEffect } from 'react';
import storage from '../../../../../hooks/useStorage/useStorage';
import LangEnum from '../../../../../lang/LangEnum';
import { getOfflineEncryptionKey } from '../../../../../services/modules/offline/offline.repository';
import { useFindTeams } from '../../../../../services/modules/users/users.queries';
import snackbarStore from '../../../../../stores/Snackbar.store';
import { Team } from '../../../../context/ApplicationContext/applicationContext.types';
import { useTranslation } from '../../../../translation/TranslationContext';
import { useNetworkContext } from '../../../contexts/network/network.context';
import { getOfflineDownloadStatusStoreBasedOnTeam } from '../../useOfflineDataDownloader/stores/offlineDownloadStatus/offlineDownloadStatus.store';

export const useHandleOfflineEncryptionKeysToOtherTeams = ({
  currentTeamId,
}: UseHandleOfflineEncryptionKeysToOtherTeamsParams) => {
  const { translate } = useTranslation();
  const { handleMessage } = snackbarStore;

  const { isOnline } = useNetworkContext();
  const { data: allTeamsData } = useFindTeams();

  const validateIfShouldGetEncryptionKeyForTeam = (teamId: string) => {
    return new Promise((resolve) => {
      const offlineDownloadStatusStore = getOfflineDownloadStatusStoreBasedOnTeam(teamId);

      if (offlineDownloadStatusStore.persist.hasHydrated()) {
        resolve(!!offlineDownloadStatusStore.getState().cachedData?.finishedDownloadAt);
        return;
      }

      const unsubscribe = offlineDownloadStatusStore.persist.onFinishHydration((state) => {
        resolve(!!state.cachedData?.finishedDownloadAt);
        unsubscribe();
      });

      offlineDownloadStatusStore.persist.rehydrate();
    });
  };

  const getEncryptionKeyAndSaveInStorage = async ({
    teamId,
    brandId,
  }: GetEncryptionKeyAndSaveInStorageParams) => {
    const response = await getOfflineEncryptionKey({ overrideHeaderParams: { teamId, brandId } });

    if (!response.ok || !response.data) {
      console.error('error fetching offline encryption key for team:', teamId);

      handleMessage({
        type: 'error',
        message: translate(LangEnum.OFFLINE_ENCRYPTION_KEY_GET_ERROR),
      });

      return;
    }

    const encryptionKey = response.data;
    storage.setOfflineEncryptionKey(teamId, encryptionKey);
  };

  const handleOfflineEncryptionKeys = async (teams: Team[]) => {
    for (const team of teams) {
      const shouldGetKey = await validateIfShouldGetEncryptionKeyForTeam(team.id);

      if (shouldGetKey && team.brand) {
        await getEncryptionKeyAndSaveInStorage({ teamId: team.id, brandId: team.brand.id });
      }
    }
  };

  useEffect(() => {
    if (!allTeamsData || !currentTeamId || !isOnline) {
      return;
    }

    const teamsWithoutCurrentTeam = allTeamsData.data.filter((team) => team.id !== currentTeamId);

    handleOfflineEncryptionKeys(teamsWithoutCurrentTeam);
  }, [allTeamsData, currentTeamId]);
};

interface GetEncryptionKeyAndSaveInStorageParams {
  teamId: string;
  brandId: string;
}

interface UseHandleOfflineEncryptionKeysToOtherTeamsParams {
  currentTeamId?: string;
}
